<template>
  <router-link
    tag="a"
    :to="link"
    class="notification-item"
    :class="classList"
    @click.capture.native="onClick"
  >
    <header class="notification-item__header">
      <div class="notification-item__title">
        <transition name="fade">
          <div v-if="!isRead" class="notification-item__new-notification">
            <span class="notification-item__new-notification-dot"></span>
          </div>
        </transition>
        <i18n path="notifications.item.heading" tag="div" class="notification-item__title-text">
          <template #type>
            <span class="notification-item__title-text-type" notification-item__title-type>{{
              $t(`notifications.item.type.${typeKey}`)
            }}</span>
          </template>
          <template #value>
            <span class="notification-item__title-text-value">{{ instanceName }}</span>
          </template>
        </i18n>
      </div>
      <div class="notification-item__meta">
        <span class="notification-item__tag">{{ tag }}</span>
        <notifications-item-actions
          v-if="!hideActions"
          ref="actions"
          class="notification-item__actions"
          :notification="notification"
          @update="$emit('update', $event)"
        />
      </div>
    </header>
    <div class="notification-item__body">
      <NotificationsItemChatResponse
        v-if="
          notification.type === NotificationType.CHAT_REPLY ||
          notification.type === NotificationType.CHAT_MENTION
        "
        :notification="notification"
      ></NotificationsItemChatResponse>
      <NotificationsItemStudyMessage
        v-if="notification.type === NotificationType.STUDY_REPLY"
        :notification="notification"
      ></NotificationsItemStudyMessage>
      <NotificationsItemStudyFollowUp
        v-if="notification.type === NotificationType.STUDY_FOLLOW_UP"
        :notification="notification"
      />
      <NotificationsItemChatReaction
        v-if="notification.type === NotificationType.CHAT_RESPONSE_REACTION"
        :notification="notification"
      />
    </div>
  </router-link>
</template>

<script>
import { NotificationModel } from '@/models/notification'
import { NotificationType } from '@/common/enums'
import NotificationsItemChatResponse from '@/components/modules/notifications/item/notifications-item-chat-response.vue'
import NotificationsItemStudyMessage from '@/components/modules/notifications/item/notifications-item-study-message.vue'
import NotificationsItemActions from '@/components/modules/notifications/item/notifications-item-actions.vue'
import NotificationsItemChatReaction from '@/components/modules/notifications/item/notifications-item-chat-reaction.vue'
import NotificationsItemStudyFollowUp from '@/components/modules/notifications/item/notifications-item-study-follow-up.vue'

export default {
  name: 'NotificationItem',
  components: {
    NotificationsItemChatReaction,
    NotificationsItemStudyFollowUp,
    NotificationsItemActions,
    NotificationsItemStudyMessage,
    NotificationsItemChatResponse
  },
  props: {
    notification: { type: NotificationModel, required: true },
    hideActions: { type: Boolean, default: false }
  },
  computed: {
    link() {
      switch (this.notification.type) {
        case NotificationType.CHAT_RESPONSE_REACTION:
          return `/chats/${this.notification.payload.chatResponseReaction.thread.chatId}/threads/${this.notification.payload.chatResponseReaction.thread.id}?goToResponseId=${this.notification.payload.chatResponseReaction.response.id}`
        case NotificationType.CHAT_REPLY:
        case NotificationType.CHAT_MENTION:
          return `/chats/${this.notification.payload.chatMention.thread.chatId}/threads/${this.notification.payload.chatMention.thread.id}?goToResponseId=${this.notification.payload.chatMention.response.id}`
        case NotificationType.STUDY_REPLY:
          return `/study/${this.notification.payload.studyReply.study.id}?goToTopicId=${this.notification.payload.studyReply.topic.id}&expandQuestionId=${this.notification.payload.studyReply.question.id}&goToMessageId=${this.notification.payload.studyReply.message.id}`
        case NotificationType.STUDY_FOLLOW_UP: {
          const followUp = this.notification.payload.studyFollowUp
          const params = []
          if (followUp.topicId && followUp.questionId) {
            params.push(`goToTopicId=${followUp.topicId}`, `goToQuestionId=${followUp.questionId}`)
          }
          return `/study/${followUp.study.id}${params.length ? `?${params.join('&')}` : ''}`
        }
      }
      return ''
    },
    isRead() {
      return this.notification.isRead
    },
    NotificationType() {
      return NotificationType
    },
    instanceName() {
      const types = {
        [NotificationType.CHAT_MENTION]: this.notification.payload?.chatMention?.chat?.name,
        [NotificationType.CHAT_REPLY]: this.notification.payload?.chatMention?.chat?.name,
        [NotificationType.STUDY_REPLY]: this.notification.payload?.studyReply?.study?.name,
        [NotificationType.STUDY_FOLLOW_UP]: this.notification.payload?.studyFollowUp?.study?.name,
        [NotificationType.CHAT_RESPONSE_REACTION]: this.notification.payload?.chatResponseReaction?.chat?.name
      }

      return types[this.notification.type]
    },
    typeKey() {
      const types = {
        [NotificationType.CHAT_MENTION]: 'chat_mention',
        [NotificationType.CHAT_REPLY]: 'chat_reply',
        [NotificationType.STUDY_REPLY]: 'study_reply',
        [NotificationType.STUDY_FOLLOW_UP]: 'study_follow_up',
        [NotificationType.CHAT_RESPONSE_REACTION]: 'chat_reaction'
      }
      return types[this.notification.type]
    },
    tag() {
      const types = {
        [NotificationType.CHAT_MENTION]: this.$t('notifications.item.tag.chat'),
        [NotificationType.CHAT_REPLY]: this.$t('notifications.item.tag.chat'),
        [NotificationType.STUDY_REPLY]: this.$t('notifications.item.tag.study'),
        [NotificationType.STUDY_FOLLOW_UP]: this.$t('notifications.item.tag.study'),
        [NotificationType.CHAT_RESPONSE_REACTION]: this.$t('notifications.item.tag.chat')
      }
      return types[this.notification.type]
    },
    classList() {
      const className = 'notification-item'
      return {
        [`${className}_new`]: !this.isRead,
        [`${className}_read`]: this.isRead
      }
    }
  },
  methods: {
    onClick(e) {
      const path = e.composedPath()
      if (path.includes(this.$refs.actions.$el)) {
        e.preventDefault()
        return
      }
      this.$refs.actions.setReadState(true)
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-item {
  $root: &;
  @apply border border-neutral-01-50 rounded relative transition duration-100 ease-in-out;
  cursor: pointer !important;
  * {
    cursor: pointer !important;
  }
  &__header {
    @apply flex items-center justify-between border-neutral-01-50 border-b bg-neutral-01-15 px-3 py-1 min-w-0;
  }
  &__title {
    @apply flex-1 flex items-center relative min-w-0 mr-6;
    &-text {
      @apply truncate text-body-03 text-primary-01-400 min-w-0;
      @apply transition duration-100 ease-in-out;
      transform: translateX(0);
      #{$root}_new & {
        transform: translateX(16px);
      }
      &-type {
        @apply text-body-02;
      }
      &-value {
        @apply underline;
      }
    }
  }
  &__meta {
    @apply flex items-center gap-2 flex-shrink-0;
  }
  &__tag {
    @apply text-body-03 text-primary-01-400;
  }
  &__actions {
    @apply flex items-center gap-2;
  }
  &_read {
    #{$root}__title-text,
    #{$root}__body,
    #{$root}__tag {
      @apply opacity-60;
    }

    #{$root}__meta,
    #{$root}__actions {
      @apply opacity-100;
    }

    #{$root}__title-text-type {
      @apply text-body-03;
    }
    #{$root}__body {
      ::v-deep * {
        @apply text-primary-01-400;
      }
    }
  }
  &__new-notification {
    @apply absolute left-0 top-0 h-full flex items-center;
    &.fade-enter-active,
    &.fade-leave-active {
      transition: opacity 0.1s;
    }
    &.fade-enter, &.fade-leave-to /* .fade-leave-active before version 2.1.8 */ {
      opacity: 0;
    }
    &-dot {
      @apply w-2 h-2 bg-accent-01 rounded-full;
    }
  }
  &__body {
    @apply px-3 py-2;
  }
}
</style>
